// src/features/Activate.js

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Typography,
  Alert,
  Box,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  RegistrationCard,
  FruitifyStack,
  FruitifyContainer,
} from "../../../shared-components/Containers";
import Stack from "@mui/material/Stack";
import FruitifyLogo from "../../../shared-components/FruitifyLogo";
import { useApiClient } from "../../../api";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import { enqueueSnackbar } from "notistack";
import { useAuthenticationStore } from "../../../stores/auth";
import { useTrackingAnalytics } from "../../../hooks/useTrackingAnalytics";

const Activate = () => {
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [successful, setSuccessful] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();
  const { uid, token } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isIndependent = searchParams.get("isIndependent") === "true";
  const apiClient = useApiClient();
  const { user, updateUserInfo } = useAuthenticationStore();
  const hasValidatedRef = useRef(false);
  const { recordMarketingEvent } = useTrackingAnalytics();

  useEffect(() => {
    if (!uid || !token) {
      setError("Invalid link.");
      setLoading(false);
      return;
    }
    setError(null);
  }, [uid, token]);

  const validateToken = async () => {
    if (hasValidatedRef.current) return;
    hasValidatedRef.current = true;
    if (!uid || !token) return;
    setLoading(true);
    try {
      const data = await apiClient.users.activate({
        uid,
        token,
      });
      if (data.success) {
        setSuccessful(true);
        if (user.isLoggedIn && !isIndependent) {
          const userData = await apiClient.users.me();
          recordMarketingEvent("activated", {}, true);
          updateUserInfo(userData);
          navigate(`/dashboard`);
        }
      } else {
        const message =
          data.message ||
          "Activation failed. You can request a new activation link below or contact support if the problem continues.";
        setError(message);
        enqueueSnackbar(message, { variant: "error", autoHideDuration: 4000 });
      }
    } catch (error) {
      const message =
        "Could not validate activation link. You can request a new activation link below or contact support if the problem continues.";
      setError(message);
      enqueueSnackbar(message, { variant: "error", autoHideDuration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewActivationLink = async () => {
    setResendLoading(true);
    try {
      const data = await apiClient.users.requestActivationLink({
        email: email,
      });

      if (data?.success) {
        enqueueSnackbar("Successfully requested a new activation code", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar(
          `${data?.message || "Unable to resend activation link."} Please contact support if the problem continues.`,
          {
            variant: "error",
            autoHideDuration: 3000,
          },
        );
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        "Error requesting a new activation code. Please contact support if the problem continues.",
        {
          variant: "error",
          autoHideDuration: 3000,
        },
      );
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    validateToken();
  }, [uid, token]);

  return (
    <FruitifyContainer>
      <FruitifyStack
        direction="column"
        justifyContent="space-between"
        data-testid={"fruitify-activate-page"}
        sx={{ height: "100%" }}
      >
        <RegistrationCard variant="outlined">
          <Stack direction={"row"} justifyContent={"center"}>
            <FruitifyLogo />
          </Stack>
          <Typography variant="h4" gutterBottom>
            {loading
              ? "Activating your account..."
              : successful
                ? "Activation Successful"
                : "Failed to Activate"}
          </Typography>

          {loading && (
            <Stack direction="row" justifyContent="center" sx={{ my: 2 }}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {!loading && !error && successful && (
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                if (isIndependent) {
                  navigate(`/login`);
                } else {
                  navigate(`/auth/reset_password/${uid}/${token}`);
                }
              }}
              noValidate
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                type="submit"
                fullWidth
              >
                {isIndependent ? "Login" : "Set your password"}
              </Button>
            </Box>
          )}
          {!loading && error && (
            <React.Fragment>
              <Alert severity="error">{error}</Alert>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 2,
                }}
              >
                <FormControl>
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <TextField
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                    required
                    fullWidth
                    variant="outlined"
                  />
                </FormControl>
                <Button
                  onClick={() => handleRequestNewActivationLink()}
                  disabled={!email || resendLoading}
                  variant="contained"
                  color="primary"
                  type="button"
                  fullWidth
                >
                  {resendLoading ? "Sending..." : "Re-send"}
                </Button>
              </Box>
            </React.Fragment>
          )}
        </RegistrationCard>
      </FruitifyStack>
    </FruitifyContainer>
  );
};

export default Activate;
